import React, { useState } from 'react';
import axios from 'axios';
import { useAuth, API } from '../App';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { ShieldCheck, FileText, Loader2, Check } from 'lucide-react';

const NDA_PDF = '/GCFE_Evaluation_NDA.pdf';

const UI = {
  eyebrow: { en: 'Global Co-Financing Engine · Before you start', fr: 'Global Co-Financing Engine · Avant de commencer' },
  title: { en: 'A few rules, then you’re in', fr: 'Quelques règles, puis c’est parti' },
  subtitle: { en: 'Evaluation environment — full agreement: GCFE-NDA-EVAL-v3.0', fr: 'Environnement d’évaluation — accord complet : GCFE-NDA-EVAL-v3.0' },
  intro: {
    en: 'GCFE is being evaluated by IDFC member institutions. Before you look around, please confirm you understand these rules — this takes about 15 seconds.',
    fr: 'GCFE est en cours d’évaluation par les institutions membres de l’IDFC. Avant de découvrir l’outil, merci de confirmer que vous comprenez ces règles — cela prend environ 15 secondes.',
  },
  keyObligations: { en: 'The rules', fr: 'Les règles' },
  checkbox: {
    en: 'I understand and agree to these rules.',
    fr: 'J’ai compris et j’accepte ces règles.',
  },
  signedIn: { en: 'Signed in as', fr: 'Connecté en tant que' },
  accept: { en: 'Agree & Continue', fr: 'Accepter et continuer' },
  toast: { en: 'Thanks — welcome to GCFE.', fr: 'Merci — bienvenue sur GCFE.' },
  error: { en: 'Something went wrong. Please try again.', fr: 'Une erreur est survenue. Veuillez réessayer.' },
  readFull: { en: 'Read the agreement (PDF)', fr: 'Lire l’accord (PDF)' },
  prevail: {
    en: 'Summary — the full agreement (EN) prevails.',
    fr: 'Résumé — l’accord complet (EN) fait foi.',
  },
};

const OBLIGATIONS = [
  { en: 'Use the environment only for the permitted evaluation purpose.', fr: 'Utiliser l’environnement uniquement pour la finalité d’évaluation autorisée.' },
  { en: 'Keep your credentials personal and use only your own account.', fr: 'Conserver vos identifiants personnels et n’utiliser que votre propre compte.' },
  { en: 'Submit only Demonstration Data — never real, non-public data.', fr: 'Ne soumettre que des données de démonstration — jamais de données réelles non publiques.' },
  { en: 'No security testing, scanning or reverse-engineering without written consent.', fr: 'Aucun test de sécurité, balayage ou rétro-ingénierie sans accord écrit.' },
  { en: 'Do not share screenshots, exports or any Platform content externally.', fr: 'Ne pas partager de captures, exports ou contenu de la plateforme à l’extérieur.' },
];

const NdaAcceptance = () => {
  const { user, loading, checkAuth } = useAuth();
  // Toujours anglais par défaut ici, même si la langue du site a déjà été basculée en
  // français automatiquement selon l'institution : ne suit PAS `useLang()`. Seul le clic
  // sur "FR" ci-dessous (choix explicite de l'analyste) passe cet écran en français.
  const [docLang, setDocLang] = useState('en');
  const [checked, setChecked] = useState(false);
  const [saving, setSaving] = useState(false);
  const t = (o) => o[docLang];

  const path = window.location.pathname;
  const blockedPaths = path === '/login' || path.startsWith('/reset-password') || path.startsWith('/accept-invitation') || path.startsWith('/verify-certificate');

  if (loading || !user || blockedPaths) return null;

  const accepted = user.nda_accepted_at && (!user.current_nda_version || user.nda_version === user.current_nda_version);
  if (accepted) return null;

  const accept = async () => {
    if (!checked) return;
    setSaving(true);
    try {
      await axios.post(`${API}/users/me/accept-nda`, { language: docLang });
      await checkAuth();
      toast.success(t(UI.toast));
    } catch {
      toast.error(t(UI.error));
      setSaving(false);
    }
  };

  return (
    <div
      data-testid="nda-acceptance-overlay"
      className="fixed inset-0 z-[10100] bg-[#0A1E29]/95 backdrop-blur-sm flex items-center justify-center p-4"
    >
      <div className="w-full max-w-lg bg-[#112C38] border border-[#285D71] rounded-xl shadow-2xl flex flex-col max-h-[92vh] overflow-hidden">
        {/* Header */}
        <div className="px-7 py-6 border-b border-[#285D71]/70 bg-[#0F2733]">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-2 text-[#45B08F] text-xs tracking-[0.2em] uppercase font-semibold mb-2">
              <ShieldCheck className="w-4 h-4" />
              {t(UI.eyebrow)}
            </div>
            <div className="flex gap-1 bg-[#0A1E29] border border-[#285D71] rounded-full p-0.5 shrink-0" data-testid="nda-lang-toggle">
              {['en', 'fr'].map((l) => (
                <button
                  key={l}
                  type="button"
                  onClick={() => setDocLang(l)}
                  data-testid={`nda-lang-${l}`}
                  className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                    docLang === l ? 'bg-[#45B08F] text-[#0A2540]' : 'text-slate-300 hover:text-white'
                  }`}
                >
                  {l === 'en' ? 'EN' : 'FR'}
                </button>
              ))}
            </div>
          </div>
          <h1 className="text-2xl font-semibold text-white leading-tight" data-testid="nda-title">
            {t(UI.title)}
          </h1>
          <p className="text-sm text-slate-400 mt-2">{t(UI.intro)}</p>
        </div>

        {/* Rules */}
        <div className="flex-1 min-h-0 flex flex-col px-7 py-5 gap-3 overflow-y-auto">
          <div
            data-testid="nda-document-frame"
            className="rounded-lg border border-[#285D71] bg-[#0F2733] px-5 py-4"
          >
            <p className="text-[11px] tracking-widest uppercase text-slate-500 mb-2">{t(UI.keyObligations)}</p>
            <ul className="space-y-2">
              {OBLIGATIONS.map((o, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-slate-200">
                  <Check className="w-4 h-4 text-[#45B08F] mt-0.5 shrink-0" />
                  <span>{t(o)}</span>
                </li>
              ))}
            </ul>
          </div>

          <p className="text-xs text-slate-500 flex items-start gap-1.5">
            <FileText className="w-3.5 h-3.5 shrink-0 mt-0.5" />
            <span>
              {t(UI.prevail)}{' '}
              <a
                href={NDA_PDF}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="nda-download-link"
                className="text-[#45B08F] hover:text-[#5fc7a4] underline underline-offset-2 transition-colors"
              >
                {t(UI.readFull)}
              </a>
            </span>
          </p>
        </div>

        {/* Footer */}
        <div className="px-7 py-5 border-t border-[#285D71]/70 bg-[#0F2733] space-y-4">
          <button
            type="button"
            onClick={() => setChecked((c) => !c)}
            data-testid="nda-accept-checkbox"
            className="flex items-start gap-3 text-left w-full group"
          >
            <span
              className={`mt-0.5 w-5 h-5 rounded flex-shrink-0 border flex items-center justify-center transition-colors ${
                checked ? 'bg-[#45B08F] border-[#45B08F]' : 'border-[#3d6b7e] group-hover:border-[#45B08F]'
              }`}
            >
              {checked && <Check className="w-3.5 h-3.5 text-[#0A2540]" strokeWidth={3} />}
            </span>
            <span className="text-sm text-slate-200">{t(UI.checkbox)}</span>
          </button>

          <div className="flex items-center justify-between gap-4">
            <p className="text-xs text-slate-500">
              {t(UI.signedIn)} <span className="text-slate-300">{user.email}</span>
            </p>
            <Button
              onClick={accept}
              disabled={!checked || saving}
              data-testid="nda-accept-btn"
              className="bg-[#45B08F] hover:bg-[#3a9a7c] text-[#0A2540] font-semibold px-6 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ShieldCheck className="w-4 h-4 mr-2" />}
              {t(UI.accept)}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NdaAcceptance;
